import React, { useEffect, useMemo, useRef, useState } from 'react';
import PopoverLayer from './popoverLayer';

const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

// Dates are handled as local Y/M/D throughout. `new Date('2026-10-22')` parses as
// UTC and can land on the previous day west of Greenwich, so the ISO string is
// split by hand instead.
const toISO = date => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

const parseISO = isoString => {
  const [year, month, day] = String(isoString || '').split('-').map(Number);
  if (!year || !month || !day) return null;
  const parsed = new Date(year, month - 1, day);
  return isNaN(parsed) ? null : parsed;
};

const startOfToday = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
};

// Same display the native date input used in this locale, so nothing reads
// differently on screen than it did before.
const formatForDisplay = isoString => {
  const date = parseISO(isoString);
  if (!date) return '';
  return `${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getDate()).padStart(2, '0')}/${date.getFullYear()}`;
};

// `min` defaults to today: every date these forms ask for (link expiry, tentative
// start date) is a future date. It used to default to `{}`, which is truthy, so the
// input rendered min="[object Object]" and past dates were accepted.
const FloatingLabelDate = ({ label, handleChange, value, name, id, required, min = '', disabled = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const rootRef = useRef(null);
  const panelRef = useRef(null);

  const minDate = useMemo(() => parseISO(min) || startOfToday(), [min]);
  const selectedDate = useMemo(() => parseISO(value), [value]);
  const today = useMemo(() => startOfToday(), []);

  const [viewDate, setViewDate] = useState(() => selectedDate || minDate);

  // Reopening on a different value should land on that month, not the last one browsed.
  useEffect(() => {
    if (isOpen) setViewDate(selectedDate || minDate);
  }, [isOpen, selectedDate, minDate]);

  useEffect(() => {
    if (!isOpen) return undefined;
    // The calendar lives in a body portal, so it is outside rootRef — it has to
    // count as "inside" or mousedown would close it before a day's click landed.
    const handleOutside = event => {
      const insideField = rootRef.current?.contains(event.target);
      const insidePanel = panelRef.current?.contains(event.target);
      if (!insideField && !insidePanel) setIsOpen(false);
    };
    // Capture phase + stopPropagation so Escape closes only the calendar. ModalShell
    // listens for Escape on document too, and without this the surrounding modal
    // would close along with the calendar.
    const handleKey = event => {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      setIsOpen(false);
    };
    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('keydown', handleKey, true);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('keydown', handleKey, true);
    };
  }, [isOpen]);

  const emit = nextValue => handleChange?.({ target: { name, id, value: nextValue } });

  const selectDate = date => {
    emit(toISO(date));
    setIsOpen(false);
  };

  const shiftMonth = offset => setViewDate(current => new Date(current.getFullYear(), current.getMonth() + offset, 1));

  // Six fixed weeks keep the popup from resizing as months change.
  const days = useMemo(() => {
    const firstOfMonth = new Date(viewDate.getFullYear(), viewDate.getMonth(), 1);
    const gridStart = new Date(firstOfMonth);
    gridStart.setDate(1 - firstOfMonth.getDay());
    return Array.from({ length: 42 }, (unused, index) => {
      const date = new Date(gridStart);
      date.setDate(gridStart.getDate() + index);
      return date;
    });
  }, [viewDate]);

  const isSameDay = (a, b) => Boolean(a && b) && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  const todayIsSelectable = today >= minDate;

  return (
    <div className="relative" ref={rootRef}>
      {/* Kept so the form still validates and submits exactly as it did with the
          plain date input — the button below is only the visible control. */}
      <input
        type="date"
        tabIndex={-1}
        className="pointer-events-none absolute inset-0 h-full w-full opacity-0"
        id={`${id || name || 'date'}-native`}
        name={name}
        value={value || ''}
        onChange={handleChange}
        min={min || toISO(today)}
        required={required}
        disabled={disabled}
      />

      <button
        type="button"
        id={id}
        onClick={() => !disabled && setIsOpen(open => !open)}
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        className={`flex min-h-12 w-full items-center justify-between gap-3 rounded-xl border bg-white px-3.5 text-left text-sm shadow-sm transition-all focus:outline-none focus:ring-2 focus:ring-orange-300 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400 ${isOpen ? 'border-[#fb6635] ring-1 ring-[#fb6635]' : 'border-slate-300 hover:border-slate-400'}`}
      >
        <span className={value ? 'font-medium text-[#211b4f]' : 'text-slate-400'}>
          {formatForDisplay(value) || 'mm/dd/yyyy'}
        </span>
        <svg className="h-4 w-4 flex-none text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 2v4m8-4v4M3 10h18M5 6h14a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2Z" />
        </svg>
      </button>

      <label htmlFor={id} className="pointer-events-none absolute -top-2 left-3 z-10 bg-white px-1 text-[11px] font-medium text-slate-500">
        {label}{required && <span className="text-red-600">*</span>}
      </label>

      {isOpen && (
        <PopoverLayer anchorRef={rootRef} panelRef={panelRef}>
        <div ref={panelRef} className="w-[19rem] rounded-2xl border border-slate-200 bg-white p-3 shadow-xl" role="dialog" aria-label={label}>
          <div className="mb-2 flex items-center justify-between">
            <p className="text-sm font-bold text-[#211b4f]">
              {MONTHS[viewDate.getMonth()]} {viewDate.getFullYear()}
            </p>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => shiftMonth(-1)}
                className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-slate-500 transition-colors hover:bg-orange-50 hover:text-[#fb6635]"
                aria-label="Previous month"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" /></svg>
              </button>
              <button
                type="button"
                onClick={() => shiftMonth(1)}
                className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-slate-500 transition-colors hover:bg-orange-50 hover:text-[#fb6635]"
                aria-label="Next month"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" /></svg>
              </button>
            </div>
          </div>

          <div className="grid grid-cols-7 gap-0.5">
            {WEEKDAYS.map(weekday => (
              <div key={weekday} className="flex h-8 items-center justify-center text-[11px] font-semibold uppercase text-slate-400">
                {weekday}
              </div>
            ))}

            {days.map(date => {
              const outsideMonth = date.getMonth() !== viewDate.getMonth();
              const isDisabled = date < minDate;
              const isSelected = isSameDay(date, selectedDate);
              const isToday = isSameDay(date, today);

              let dayStyles = 'text-[#211b4f] hover:bg-orange-50 hover:text-[#fb6635]';
              if (isDisabled) dayStyles = 'cursor-not-allowed text-slate-300';
              else if (isSelected) dayStyles = 'bg-[#211b4f] font-bold text-white hover:bg-[#30266c]';
              else if (outsideMonth) dayStyles = 'text-slate-400 hover:bg-orange-50';

              return (
                <button
                  key={toISO(date)}
                  type="button"
                  disabled={isDisabled}
                  onClick={() => selectDate(date)}
                  aria-current={isToday ? 'date' : undefined}
                  className={`flex h-9 items-center justify-center rounded-lg text-sm transition-colors ${dayStyles} ${isToday && !isSelected ? 'ring-1 ring-inset ring-[#fb6635]' : ''}`}
                >
                  {date.getDate()}
                </button>
              );
            })}
          </div>

          <div className="mt-2 flex items-center justify-between border-t border-slate-100 pt-2">
            <button
              type="button"
              onClick={() => { emit(''); setIsOpen(false); }}
              className="rounded-lg px-2 py-1 text-xs font-semibold text-[#fb6635] transition-colors hover:bg-orange-50"
            >
              Clear
            </button>
            <button
              type="button"
              disabled={!todayIsSelectable}
              onClick={() => selectDate(today)}
              className="rounded-lg px-2 py-1 text-xs font-semibold text-[#211b4f] transition-colors hover:bg-slate-100 disabled:cursor-not-allowed disabled:text-slate-300 disabled:hover:bg-transparent"
            >
              Today
            </button>
          </div>
        </div>
        </PopoverLayer>
      )}
    </div>
  );
};

export default FloatingLabelDate;
